import { IconExternalLink } from "@tabler/icons-react";

import type { WorkspaceConnection } from "../../api/types";
import { CopyBlock } from "../ui/CopyBlock";

interface ConnectionBlockProps {
  connection: WorkspaceConnection;
  /** Compact form for the home screen: the SSH line and VS Code only. */
  compact?: boolean;
}

/**
 * How to get into the workspace: one command, or one button.
 *
 * The VS Code link carries the host, the port and the user, so it works with no
 * `~/.ssh/config` entry at all — including from the home screen, where the
 * snippet is not shown.
 *
 * Args:
 *     connection: The connection details served with the workspace.
 *     compact: Hide the `~/.ssh/config` snippet (home screen).
 *
 * Returns:
 *     The connection block.
 */
export function ConnectionBlock({
  connection,
  compact = false,
}: ConnectionBlockProps): JSX.Element {
  return (
    <div className="connect">
      <CopyBlock
        value={connection.ssh_command}
        label="Copy the SSH command"
        multiline={!compact}
      />

      {/*
        The route reaches the door up to a minute after the workspace becomes
        ready (deploy/manifests/ssh-proxy.yaml). Without this line, the most
        likely first experience of the product is "Connection refused" next to
        a screen saying "Ready", with nothing on it explaining the gap.
      */}
      <p className="connect__hint">
        If the first attempt is refused, wait a minute and try again — on a
        brand-new workspace the door takes a moment to open.
      </p>

      {/*
        A name can fail to resolve for reasons the researcher cannot fix and
        cannot see: a resolver still holding a negative answer for a fresh
        record, a corporate DNS, a VPN. The address is the same door, so it is
        offered rather than left to be discovered — quietly, because the name
        is what survives the LoadBalancer being recreated.
      */}
      {connection.ssh_command_ip ? (
        <details className="connect__fallback">
          <summary>Name not resolving?</summary>
          <p className="connect__hint">
            Same machine, reached by address instead. Use the command above
            once your computer knows the name — an address can change if the
            door is rebuilt.
          </p>
          <CopyBlock
            value={connection.ssh_command_ip}
            label="Copy the SSH command using the address"
            multiline={!compact}
          />
        </details>
      ) : null}

      {!compact ? (
        <div className="connect__config">
          <p className="connect__hint">
            Add this to <span className="mono">~/.ssh/config</span> and{" "}
            <span className="mono">scp</span> knows this workspace by name. The
            name changes with every workspace on purpose, so an old entry can
            never send you to somebody else&apos;s machine — delete the ones you
            no longer use.
          </p>
          <CopyBlock
            value={connection.ssh_config}
            label="Copy the SSH config entry"
            multiline
          />
        </div>
      ) : null}

      <a className="btn btn--block" href={connection.vscode_url}>
        <IconExternalLink size={14} aria-hidden="true" />
        Open in VS Code
      </a>
    </div>
  );
}
